import { ExternalLink, Phone } from 'lucide-react';
import { cn } from '@ai-tutor/ui';
import { FIND_A_HELPLINE, HELPLINES } from './helplines';

/**
 * Where a student goes when it is more than a study problem: the body of
 * `HelplineDialog`.
 *
 * Calm on purpose: no red, no alarm icon. Each service is one row - who they
 * are, where and when, and the number as a single tap-to-call button, so a
 * phone dials it in one tap. It says plainly that Xonera is not the help,
 * and hands over to people who are.
 */
export function SupportPanel({ className }: { className?: string }) {
  return (
    <div className={cn('space-y-5 text-sm', className)}>
      <p className='text-muted-foreground'>
        Talking helps. These services are free and confidential, and the people
        on the other end are trained to listen.
      </p>
      <ul className='space-y-2'>
        {HELPLINES.map(line => (
          <li
            key={line.name}
            className='flex items-center gap-4 rounded-xl bg-secondary/60 px-4 py-3 dark:bg-secondary/40'
          >
            <div className='min-w-0 flex-1'>
              <p className='font-medium text-foreground'>{line.name}</p>
              <p className='mt-0.5 text-xs text-muted-foreground'>
                {line.region} · {line.detail}
              </p>
            </div>
            <a
              href={line.href}
              aria-label={`Call ${line.name}, ${line.contact}`}
              className='tint-brand hover:tint-brand-strong inline-flex shrink-0 items-center gap-2 rounded-full px-3.5 py-2 font-medium transition-colors duration-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card max-sm:h-11'
            >
              <Phone className='h-4 w-4' aria-hidden='true' />
              <span className='font-mono tabular-nums'>{line.contact}</span>
            </a>
          </li>
        ))}
      </ul>
      <div className='space-y-1 border-t border-border/60 pt-4'>
        <p className='text-muted-foreground'>
          Somewhere else?{' '}
          <a
            href={FIND_A_HELPLINE}
            target='_blank'
            rel='noreferrer'
            className='tap-target inline-flex items-center gap-1 font-medium text-foreground underline underline-offset-4'
          >
            Find a helpline near you
            <ExternalLink className='h-3 w-3' aria-hidden='true' />
            <span className='sr-only'>(opens in a new tab)</span>
          </a>
        </p>
        <p className='text-xs text-muted-foreground'>
          If you are in danger right now, call your local emergency number.
        </p>
      </div>
    </div>
  );
}
